/**
 * Game-side PRESENTATION sidecar for the "Beat My Edit" share link.
 *
 * The core `@desk/core` challenge payload (`?c=`) encodes the scored EditGraph
 * (cut / reorder / frame-crop / caption-text / sound / look-grade / sticker /
 * cast) + pack + seed. Several OUTCOME-AFFECTING levers were added GAME-SIDE as
 * session/presentation state and never enter that graph, so a shared `?c=` link
 * does NOT reproduce them. This module carries those levers alongside the core
 * payload in a SECOND query param (`?p=`), so a shared link restores the reel +
 * "How it's landing" identically to the sharer's:
 *
 *   - Stacked EFFECTS (rain / lightning / focus-blur) — the Look-effects split.
 *   - Caption PLACEMENT zone (top / over-subject / chyron) + exact anchor.
 *   - Precision-trim per-beat (the "trim to clean end" leak trim).
 *   - The cosmetic reaction-tag emoji id (kept for byte-identical restore).
 *
 * NOTE: Focus zoom/pan crop is already carried by the core `frame` op (see
 * EditorScreen `commitFocusCrop` -> `session.applyOp({ kind: "frame", ... })`),
 * so it rides the `?c=` graph and is intentionally NOT duplicated here.
 *
 * This is a game-side sidecar: `@desk/core` is untouched. Versioned + validated;
 * malformed input is REJECTED (throws `GamePresentationError`) exactly like the
 * core payload rejects malformed `?c=`.
 *
 * Compact versioned JSON + UTF-8 base64url — no dependencies. Never executed.
 */
import type { CaptionPlacementZone } from "../emotion-model.js";
import { PLACEMENT_ZONE_ANCHORS } from "../../shell/lib/caption-placement.js";
import { effectInstanceKey, type EffectInstance } from "../../shell/lib/creative-fx.js";

/** Bumped when the sidecar shape changes; decode rejects unknown versions. */
export const GAME_PRESENTATION_VERSION = 1 as const;

/** Query key for the presentation sidecar, alongside the core `?c=`. */
export const PRESENTATION_QUERY_KEY = "p";

/** Max base64url characters accepted for the sidecar value. */
export const PRESENTATION_MAX_ENCODED_CHARS = 4_000;
/** Max UTF-8 byte length of the decoded JSON string. */
export const PRESENTATION_MAX_DECODED_BYTES = 6_000;
/** Bound list sizes so a hostile link cannot balloon memory. */
export const PRESENTATION_MAX_LIST = 64;
/** Bound each id length. */
export const PRESENTATION_MAX_ID_LEN = 64;

const PLACEMENT_ZONES: readonly CaptionPlacementZone[] = ["top", "chyron", "subject"];

export class GamePresentationError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "GamePresentationError";
    this.code = code;
  }
}

/** Game-side presentation levers restored from a shared link. */
export interface GamePresentation {
  /** Placed EFFECTS (rain / lightning / focus-blur) as {effectId, beatId}. */
  effects: EffectInstance[];
  /** Caption placement zone the reel snapped to, or null when no override. */
  captionPlacement: CaptionPlacementZone | null;
  /** Exact caption anchor (per-mille) for byte-identical restore, or null. */
  captionPos: { xMilli: number; yMilli: number } | null;
  /** Cosmetic reaction-tag emoji id, or null when none. */
  reactionId: string | null;
  /** Beats trimmed to their clean end (Precision Trim). */
  trims: string[];
}

/** The neutral sidecar — no session levers set. */
export const EMPTY_PRESENTATION: GamePresentation = {
  effects: [],
  captionPlacement: null,
  captionPos: null,
  reactionId: null,
  trims: [],
};

/** True when the sidecar carries nothing worth putting in a URL. */
export function isDefaultPresentation(p: GamePresentation): boolean {
  return (
    p.effects.length === 0 &&
    p.trims.length === 0 &&
    p.captionPlacement === null &&
    p.captionPos === null &&
    (p.reactionId === null || p.reactionId === "reaction-none")
  );
}

/** Build a sidecar from raw editor session values (defensive, sanitizing). */
export function presentationFromSession(input: {
  effects?: readonly EffectInstance[] | null;
  captionPos?: { xMilli: number; yMilli: number } | null;
  captionPlacement?: CaptionPlacementZone | null;
  reactionId?: string | null;
  trims?: readonly string[] | null;
}): GamePresentation {
  const effects = sanitizeEffectInstances(input.effects);
  const trims = sanitizeIdList(input.trims);
  const captionPos = sanitizeCaptionPos(input.captionPos ?? null);
  const captionPlacement =
    input.captionPlacement && PLACEMENT_ZONES.includes(input.captionPlacement)
      ? input.captionPlacement
      : null;
  const reactionId =
    typeof input.reactionId === "string" && input.reactionId.length > 0
      ? input.reactionId.slice(0, PRESENTATION_MAX_ID_LEN)
      : null;
  return { effects, captionPlacement, captionPos, reactionId, trims };
}

/**
 * The anchor (per-mille) to restore for a sidecar: the exact stored position
 * when present, else the canonical anchor for the stored zone, else null (the
 * caption keeps its default lower-third anchor). Presentation only.
 */
export function restoredCaptionPos(
  p: GamePresentation,
): { xMilli: number; yMilli: number } | null {
  if (p.captionPos) return { ...p.captionPos };
  if (p.captionPlacement) return { ...PLACEMENT_ZONE_ANCHORS[p.captionPlacement] };
  return null;
}

// ── encode / decode ──────────────────────────────────────────────────────────

interface WirePresentation {
  v: number;
  fx: string[];
  cz: string | null;
  cx: number | null;
  cy: number | null;
  rx: string | null;
  tr: string[];
}

/** Encode a sidecar to a base64url query value. */
export function encodeGamePresentation(p: GamePresentation): string {
  const wire: WirePresentation = {
    v: GAME_PRESENTATION_VERSION,
    fx: p.effects.map(effectInstanceKey),
    cz: p.captionPlacement,
    cx: p.captionPos ? p.captionPos.xMilli : null,
    cy: p.captionPos ? p.captionPos.yMilli : null,
    rx: p.reactionId,
    tr: p.trims,
  };
  const json = JSON.stringify(wire);
  const encoded = utf8ToBase64Url(json);
  if (encoded.length > PRESENTATION_MAX_ENCODED_CHARS) {
    throw new GamePresentationError("too-large", "presentation sidecar too large");
  }
  return encoded;
}

/** Decode + validate a sidecar; throws `GamePresentationError` when malformed. */
export function decodeGamePresentation(encoded: string): GamePresentation {
  if (typeof encoded !== "string" || encoded.length === 0) {
    throw new GamePresentationError("missing", "empty presentation payload");
  }
  if (encoded.length > PRESENTATION_MAX_ENCODED_CHARS) {
    throw new GamePresentationError("too-large", "presentation sidecar too large");
  }
  const json = base64UrlToUtf8(encoded);
  if (utf8ByteLength(json) > PRESENTATION_MAX_DECODED_BYTES) {
    throw new GamePresentationError("too-large", "decoded presentation too large");
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new GamePresentationError("malformed", "presentation is not valid JSON");
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    throw new GamePresentationError("malformed", "presentation must be an object");
  }
  const obj = raw as Record<string, unknown>;
  if (obj.v !== GAME_PRESENTATION_VERSION) {
    throw new GamePresentationError(
      "version",
      `unsupported presentation version: ${String(obj.v)}`,
    );
  }
  const effects = requireIdList("fx", obj.fx)
    .map(parseEffectInstance)
    .filter((e): e is EffectInstance => e !== null);
  const trims = requireIdList("tr", obj.tr);
  const captionPlacement = requireZone("cz", obj.cz);
  const cx = requireAnchorMilliOrNull("cx", obj.cx);
  const cy = requireAnchorMilliOrNull("cy", obj.cy);
  const reactionId = requireIdOrNull("rx", obj.rx);
  const captionPos =
    cx !== null && cy !== null ? { xMilli: cx, yMilli: cy } : null;
  if ((cx === null) !== (cy === null)) {
    throw new GamePresentationError("malformed", "caption anchor must set both x and y");
  }
  return { effects, captionPlacement, captionPos, reactionId, trims };
}

// ── validators ───────────────────────────────────────────────────────────────

/** Parse a wire "effectId@beatId" into an instance; null for a legacy bare id. */
function parseEffectInstance(s: string): EffectInstance | null {
  const at = s.lastIndexOf("@");
  if (at <= 0 || at >= s.length - 1) return null; // old bare-id / malformed → drop
  return { effectId: s.slice(0, at), beatId: s.slice(at + 1) };
}

/** Defensive sanitize of session effect instances (dedupe, bound id lengths). */
function sanitizeEffectInstances(
  v: readonly EffectInstance[] | null | undefined,
): EffectInstance[] {
  if (!Array.isArray(v)) return [];
  const out: EffectInstance[] = [];
  const seen = new Set<string>();
  for (const i of v) {
    if (!i || typeof i.effectId !== "string" || typeof i.beatId !== "string") continue;
    if (i.effectId.length === 0 || i.beatId.length === 0) continue;
    const inst = {
      effectId: i.effectId.slice(0, PRESENTATION_MAX_ID_LEN),
      beatId: i.beatId.slice(0, PRESENTATION_MAX_ID_LEN),
    };
    const key = effectInstanceKey(inst);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(inst);
    if (out.length >= PRESENTATION_MAX_LIST) break;
  }
  return out;
}

function sanitizeIdList(v: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== "string" || item.length === 0) continue;
    const id = item.slice(0, PRESENTATION_MAX_ID_LEN);
    if (!out.includes(id)) out.push(id);
    if (out.length >= PRESENTATION_MAX_LIST) break;
  }
  return out;
}

function sanitizeCaptionPos(
  v: { xMilli: number; yMilli: number } | null,
): { xMilli: number; yMilli: number } | null {
  if (!v) return null;
  const x = clampAnchor(v.xMilli);
  const y = clampAnchor(v.yMilli);
  if (x === null || y === null) return null;
  return { xMilli: x, yMilli: y };
}

function clampAnchor(n: unknown): number | null {
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  return Math.max(0, Math.min(1000, Math.round(n)));
}

function requireIdList(path: string, v: unknown): string[] {
  if (!Array.isArray(v)) {
    throw new GamePresentationError("malformed", `${path} must be an array`);
  }
  if (v.length > PRESENTATION_MAX_LIST) {
    throw new GamePresentationError("malformed", `${path} too long`);
  }
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== "string" || item.length === 0 || item.length > PRESENTATION_MAX_ID_LEN) {
      throw new GamePresentationError("malformed", `${path} entries must be non-empty ids`);
    }
    if (!out.includes(item)) out.push(item);
  }
  return out;
}

function requireZone(path: string, v: unknown): CaptionPlacementZone | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== "string" || !PLACEMENT_ZONES.includes(v as CaptionPlacementZone)) {
    throw new GamePresentationError("malformed", `${path} must be a caption placement zone`);
  }
  return v as CaptionPlacementZone;
}

function requireAnchorMilliOrNull(path: string, v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== "number" || !Number.isFinite(v) || !Number.isInteger(v) || v < 0 || v > 1000) {
    throw new GamePresentationError("malformed", `${path} must be a 0..1000 integer`);
  }
  return v;
}

function requireIdOrNull(path: string, v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== "string" || v.length === 0 || v.length > PRESENTATION_MAX_ID_LEN) {
    throw new GamePresentationError("malformed", `${path} must be a non-empty id`);
  }
  return v;
}

// ── base64url (game-side; mirrors the core helper, no @desk/core dependency) ──

function utf8ByteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

function utf8ToBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  const b64 =
    typeof btoa === "function" ? btoa(binary) : Buffer.from(bytes).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToUtf8(encoded: string): string {
  const padded = encoded + "=".repeat((4 - (encoded.length % 4)) % 4);
  const b64 = padded.replace(/-/g, "+").replace(/_/g, "/");
  let binary: string;
  try {
    if (typeof atob === "function") {
      binary = atob(b64);
    } else {
      return Buffer.from(b64, "base64").toString("utf8");
    }
  } catch {
    throw new GamePresentationError("malformed", "malformed base64");
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new GamePresentationError("malformed", "invalid utf-8 in presentation");
  }
}
